<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, getCurrentInstance } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from './ModalBox.vue'
import VmixTokenService from '@/services/VmixTokenService'
import GetVmixTokenModal from '@/components/modals/GetVmixTokenModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { track, EVENTS } from '@/lib/tracking'

const MODAL_ID = 'modalCreateVmixToken'

const props = defineProps({
	modalSize: {
		type: String,
		default: 'md',
		validator(v) {
			return ['xl', 'lg', 'md', 'sm'].some((size) => size === v)
		},
	},
	cancelText: {
		type: String,
		default: 'Cancel',
	},
})

const emit = defineEmits(['modal-cancel', 'modal-dismiss', 'toggle-processing', 'token-created'])

const root = getCurrentInstance()?.proxy?.$root
const { notify } = useNotification()

const tokenName = ref('')
const vmixToken = ref({})

const { isVisible, hide } = useLegacyModalBridge(MODAL_ID, {
	onHidden: () => {
		tokenName.value = ''
	},
})

const dismiss = () => {
	tokenName.value = ''
	emit('modal-cancel')
	hide()
	emit('modal-dismiss')
}

const createToken = async () => {
	try {
		emit('toggle-processing', true)
		hide()
		vmixToken.value = await VmixTokenService.createVmixToken({ name: tokenName.value })
		track(EVENTS.VMIX_TOKEN_CREATED, {})
		showModal('modalGetVmixToken')
		emit('token-created')
	} catch (err) {
		notify({
			group: 'error',
			title: "Couldn't create vMix token",
			text: err.message,
		})

		if (err.message?.includes('upgrade')) {
			showModal('billing-prompt')
		}
	}
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		@close="dismiss"
	>
		<div class="px-6 pt-6">
			<h4 class="text-s-xl">New vMix Token</h4>
			<p class="text-s-s mb-2 mt-2">Token name</p>
			<FormInput v-model="tokenName" class="input" placeholder="" />
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6 border-t border-surface-3">
				<FormButton type="surface-4" size="md" @click="dismiss">
					{{ cancelText }}
				</FormButton>
				<FormButton type="main" size="md" :disabled="!tokenName" @click="createToken">
					Create new token
				</FormButton>
			</div>
		</template>
	</ModalBox>
	<GetVmixTokenModal :vmix-token="vmixToken" />
</template>
